import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@mui/material";

import ShareLink from "./ShareLink";

import LoadingBackdrop from "./LoadingBackdrop";

import { AIModelSelect } from "./TestComponents2";
import { AIModelInfoTooltip } from "./AIModelInfoDialog";
import ShareDialog from "./ShareDialog";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ShareIcon from "@mui/icons-material/Share";
import QuestionMarkIcon from "@mui/icons-material/Help";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PrintAsPdf from "./PrintAsPdf";
import { useSearchParams, useParams } from "react-router-dom";
import Translate from "./Translate";
import { BackButton } from "./functions";
import MoreToCome from "./MoreToCome";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import { AnimatedStep } from "./Animations";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LoadingDialog from "./LoadingDialog";

import AddIcon from "@mui/icons-material/Add";
import GenerateIcon from "@mui/icons-material/AutoAwesome";

import { handlePrintGrammarLesson } from "./printFunctions";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { callApi } from "./api";
import { useSnackbar } from "./SnackbarHandler";
import PrintIcon from "@mui/icons-material/Print";
import ProgressBar from "./ProgressBar";
import Voice from "./Voice";
import LanguageSelector from "./LanguageSelector";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CustomFormControlLabel, trackLesson } from "./functions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoBox from "./InfoBox";

import Logger from "./Logger";
import LessonDrawer from "./LessonDrawer";
import SaveLessonDialog from "./SaveLessonDialog";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import HistoryIcon from "@mui/icons-material/History";

import { db } from "./firebase";
import {
  collection,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc,
  getDocs,
} from "firebase/firestore";

const grammarConcepts = {
  satsdelar: [
    { id: "subjekt", label: "Subjekt" },
    { id: "predikat", label: "Predikat" },
    { id: "direkt_objekt", label: "Direkt objekt" },
    { id: "indirekt_objekt", label: "Indirekt objekt" },
    { id: "predikatsfyllnad", label: "Predikatsfyllnad" },
    { id: "attribut", label: "Attribut" },
    { id: "adverbial", label: "Adverbial" },
  ],

  ordklasser: [
    { id: "substantiv", label: "Substantiv" },
    { id: "verb", label: "Verb" },
    { id: "adjektiv", label: "Adjektiv" },
    { id: "adverb", label: "Adverb" },
    { id: "preposition", label: "Preposition" },
    { id: "konjunktion", label: "Konjunktion" },
    { id: "pronomen", label: "Pronomen" },
    { id: "artikel", label: "Artikel" },
    { id: "interjektion", label: "Interjektion" },
    { id: "räkneord", label: "Räkneord" },
  ],

  tempus: [
    { id: "presens", label: "Presens" },
    { id: "preteritum", label: "Preteritum" },
    { id: "perfekt", label: "Perfekt" },
    { id: "pluskvamperfekt", label: "Pluskvamperfekt" },
    { id: "futurum_simple", label: "Futurum (ska/kommer att)" },
    { id: "futurum_preteritum", label: "Futurum preteritum (skulle)" },
  ],

  modus: [
    { id: "indikativ", label: "Indikativ" },
    { id: "imperativ", label: "Imperativ" },
    { id: "konjunktiv", label: "Konjunktiv" },
  ],

  diatesis: [
    { id: "aktiv", label: "Aktiv" },
    { id: "passiv", label: "Passiv" },
  ],

  species: [
    { id: "obestämd_form", label: "Obestämd form" },
    { id: "bestämd_form", label: "Bestämd form" },
  ],

  numerus: [
    { id: "singularis", label: "Singularis" },
    { id: "pluralis", label: "Pluralis" },
  ],

  genus: [
    { id: "utrum", label: "Utrum (en-ord)" },
    { id: "neutrum", label: "Neutrum (ett-ord)" },
  ],

  kasus: [
    { id: "grundform", label: "Grundform" },
    { id: "genitiv", label: "Genitiv" },
  ],

  komparation: [
    { id: "positiv", label: "Positiv" },
    { id: "komparativ", label: "Komparativ" },
    { id: "superlativ", label: "Superlativ" },
  ],

  satstyper: [
    { id: "huvudsats", label: "Huvudsats" },
    { id: "bisats", label: "Bisats" },
  ],

  verbform: [
    { id: "infinitiv", label: "Infinitiv" },
    { id: "supinum", label: "Supinum" },
    { id: "presens_particip", label: "Presens particip" },
    { id: "perfekt_particip", label: "Perfekt particip" },
  ],

  pronomentyper: [
    { id: "personliga", label: "Personliga" },
    { id: "possessiva", label: "Possessiva" },
    { id: "demonstrativa", label: "Demonstrativa" },
    { id: "relativa", label: "Relativa" },
    { id: "interrogativa", label: "Interrogativa" },
    { id: "indefinita", label: "Indefinita" },
    { id: "reflexiva", label: "Reflexiva" },
    { id: "reciproka", label: "Reciproka" },
  ],

  ordföljd: [
    { id: "rak", label: "Rak ordföljd" },
    { id: "omvänd", label: "Omvänd ordföljd" },
    { id: "bisatsordföljd", label: "Bisatsordföljd" },
  ],
};

const GrammarLesson = ({ theme, styles, user, setMinimized }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [sentenceCount, setSentenceCount] = useState(2);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lessonData, setLessonData] = useState(null);
  const [progress, setProgress] = useState(0);
  const { showSnackbar } = useSnackbar();
  const [language, setLanguage] = useState("svenska");
  const [showTranslation, setShowTranslation] = useState(false);
  const [translationLanguage, setTranslationLanguage] = useState("arabiska");
  const [selectedConcepts, setSelectedConcepts] = useState({});
  const pdfButtonRef = useRef(null);
  const translateButtonRef = useRef(null);
  const shareLinkButtonRef = useRef(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [titleText, setTitleText] = useState("Grammatikövning");
  const [editingExampleId, setEditingExampleId] = useState(null);
  const [editingTranslationId, setEditingTranslationId] = useState(null);
  const [isEditingStory, setIsEditingStory] = useState(false);
  const [isEditingStoryTranslation, setIsEditingStoryTranslation] =
    useState(false);
  const [cardVariant, setCardVariant] = useState("classic");

  const [showAllCategories, setShowAllCategories] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategories = ["satsdelar", "ordklasser", "tempus", "verbform"];
  const viewMode = searchParams.get("v") === "t";
  const [currentStep, setCurrentStep] = useState(1);

  const [isSharing, setIsSharing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);

  useEffect(() => {
    if (lessonData) {
      setCurrentStep(3);
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [lessonData, currentStep]);

  const { lessonId } = useParams();

  useEffect(() => {
    const loadLessonFromId = async () => {
      if (lessonId) {
        try {
          const q = query(
            collection(db, "lesson_usage"),
            where("__name__", ">=", lessonId),
            where("__name__", "<=", lessonId + "\uf8ff"),
            where("lessonType", "==", "GrammarLesson.js"),
            limit(1),
          );

          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docData = querySnapshot.docs[0].data();

            if (!docData.hasOpened) {
              await updateDoc(querySnapshot.docs[0].ref, {
                hasOpened: true,
                openedAt: Date.now(),
              });
            }

            setLessonData(docData.data);

            showSnackbar("Lektionen har laddats in.", "success");
          } else {
            showSnackbar("Kunde inte hitta lektionen", "error");
          }
        } catch (error) {
          console.error("Error loading lesson:", error);
          showSnackbar(
            "Ett fel uppstod när presentationen skulle laddas",
            "error",
          );
        }
      }
    };

    loadLessonFromId();
  }, lessonId);

  const [selectedModel, setSelectedModel] = useState("Studera.AI");

  const handleSaveLessonClick = () => {
    setSaveDialogOpen(true);
  };

  const toggleViewMode = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    if (currentParams.v === "t") {
      delete currentParams.v;
    } else {
      currentParams.v = "t";
    }
    setSearchParams(currentParams);
  };

  const handleSaveWithComment = async (comment, name, coverImage, tags) => {
    Logger.log(JSON.stringify(lessonData, null, 2));
    await trackLesson(
      user.uid,
      "GrammarLesson.js",
      lessonData,
      null,
      true,
      comment,
      name,
      coverImage,
      tags,
    );
    setDrawerOpen(true);
    showSnackbar("Lektionen sparad i din lektionsbank!", "success");
  };

  const handleShare = async (
    lessonComment,
    lessonName,
    selectedImage,
    tags,
    shareData,
  ) => {
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      // Create a document for each student and then create notifications
      const sharePromises = shareData.studentIds.map(async (studentId) => {
        const documentData = {
          userId: user.uid,
          shared: true,
          sharedTo: studentId,
          comment: shareData.comment,
          sharedAt: Date.now(),
          fileUrl: shareData.fileUrl,
          lessonType: "GrammarLesson.js",
          timestamp: serverTimestamp(),
          lessonComment,
          lessonName,
          coverImage: selectedImage,
          hasPrinted: true,
          data: lessonData,
        };
        // Create lesson document and get ID
        const lessonDoc = await addDoc(
          collection(db, "lesson_usage"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );
        // Create notification with lesson doc ID
        await addDoc(collection(db, "notifications"), {
          type: "lesson_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          lessonData: {
            lessonId: lessonDoc.id,
            lessonType: documentData.lessonType,
            lessonName: documentData.lessonName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            coverImage: selectedImage,
          },
        });
        return lessonDoc;
      });
      await Promise.all(sharePromises);
      showSnackbar(`Du har nu delat lektionen!`, "success");
    } catch (error) {
      showSnackbar("Fel vid delande av lektionen: " + error.message, "error");
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleConceptChange = (category, conceptId) => {
    setSelectedConcepts((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [conceptId]: !prev[category]?.[conceptId],
      },
    }));
  };

  const simulateProgress = (
    startProgress,
    endProgress,
    duration,
    setProgressFn,
  ) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setProgressFn(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const handleGenerateGrammar = async () => {
    setIsGenerating(true);
    setProgress(0);
    setLessonData(null);
    let stopProgressSimulation = null;

    try {
      stopProgressSimulation = simulateProgress(0, 90, 15000, setProgress);

      const selectedConceptsArray = Object.entries(selectedConcepts).flatMap(
        ([category, concepts]) =>
          Object.entries(concepts)
            .filter(([_, isSelected]) => isSelected)
            .map(([conceptId]) => conceptId),
      );

      const response = await callApi(
        [
          {
            role: "system",
            content: `Du är en språklärare som specialiserar sig på grammatik och språkstruktur.`,
          },
          {
            role: "user",
            content: `Skapa en grammatiklektion med ${sentenceCount} exempelmeningar på ${language}${showTranslation ? ` med översättning till ${translationLanguage}` : ""}.
            Använd följande grammatiska koncept: ${selectedConceptsArray.join(", ")}.

            Returnera ett JSON-objekt med följande struktur:
            {
              "title": "Grammatikövning",
              "instructions": "En kort instruktion om hur man ska arbeta med meningarna",
              "sentences": [
                {
                  "sentence": "exempelmening",
                  ${showTranslation ? `"translation": "översättning",` : ""}
                  "analysis": "grammatisk analys av meningen som pekar ut de valda koncepten"
                }
              ],
              "story": "En kort berättelse (8-10 meningar) som använder de grammatiska koncepten"${showTranslation ? ',\n              "storyTranslation": "Berättelsens översättning"' : ""}
            }`,
          },
        ],
        8000,
        true,
        user.uid,
      );

      if (stopProgressSimulation) {
        stopProgressSimulation();
      }

      setProgress(95);

      let messageContent = response.data.choices[0].message.content.trim();
      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }

      const data = JSON.parse(messageContent);
      setProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 800));

      setLessonData(data);
      showSnackbar("Grammatiklektion har genererats!", "success");

      await trackLesson(user.uid, "GrammarLesson.js", null, {
        language,
        translationLanguage,

        concepts: selectedConceptsArray.join(", "),
      });
    } catch (error) {
      console.error("Error generating grammar lesson:", error);
      showSnackbar("Ett fel uppstod när lektionen skulle genereras", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: {
            xs: "100%",
            md: "100%",
          },
          margin: "0 auto",
        }}
      >
        {!viewMode && (
          <>
            {currentStep !== 3 && (
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  ...styles.newH1,
                }}
              >
                Grammatik
              </Typography>
            )}
          </>
        )}

        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {!viewMode && (
            <>
              <Tooltip title="Dina sparade lektioner">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ...styles.iconButtonBg }}
                >
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        {/*
        <InfoBox
          icon={MenuBookIcon}
          title="Instruktioner för Grammatiklektion"
          instructions={[
            "Ställ in antal exempelmeningar",
            "Välj språk för meningarna",
            "Välj grammatiska koncept att inkludera",
            "Aktivera översättning om så önskas",
          ]}
          theme={theme}
          styles={styles}
          user={user}
          lessonType="GrammarLesson.js"
        />
        */}

        {!viewMode && (
          <>
            {currentStep === 1 && (
              <>
                <AnimatedStep isRevealed={true}>
                  <Box sx={{ ...styles.headlineLessonBox, mt: 6 }}>
                    <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                      <span>1</span> Grundinformation
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      ...styles.freshBox,
                      padding: "30px",
                      width: { xs: "100%", md: "70%" },
                      margin: "0 auto",
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "500",
                            mb: 1,
                          }}
                        >
                          Antal meningar:
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={sentenceCount}
                            onChange={(e) => setSentenceCount(e.target.value)}
                          >
                            {[2, 4, 6, 8, 10].map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}&nbsp; meningar
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "500",
                            mb: 1,
                          }}
                        >
                          Språk:
                        </Typography>
                        <LanguageSelector
                          language={language}
                          setLanguage={setLanguage}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "500", mb: 1 }}
                        >
                          Visa översättning?
                        </Typography>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={showTranslation}
                              onChange={(e) =>
                                setShowTranslation(e.target.checked)
                              }
                            />
                          }
                          label={showTranslation ? "Ja" : "Nej"}
                        />
                      </Grid>
                      {showTranslation && (
                        <Grid item xs={12} md={12}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "500",
                              mb: 1,
                            }}
                          >
                            Språk för översättning:
                          </Typography>
                          <LanguageSelector
                            language={translationLanguage}
                            setLanguage={setTranslationLanguage}
                          />
                        </Grid>
                      )}
                    </Grid>
                    <Box sx={{ textAlign: "center" }}>
                      <Button
                        variant="purp"
                        sx={{ mt: 4 }}
                        onClick={() => {
                          setCurrentStep(2);
                        }}
                        endIcon={<ArrowForwardIosIcon />}
                      >
                        Nästa
                      </Button>
                    </Box>
                  </Box>
                </AnimatedStep>
              </>
            )}
          </>
        )}

        {currentStep === 2 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(currentStep - 1);

                setLessonData(null);
                setSelectedConcepts({});
              }}
              show={currentStep !== 1}
            />
            <AnimatedStep isRevealed={true}>
              <Box sx={{ ...styles.headlineLessonBox, mt: 6 }}>
                <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                  <span>2</span> Välj grammatiska koncept
                </Typography>
              </Box>

              <Box
                sx={{
                  ...styles.freshBox,
                  padding: "30px",
                  width: { xs: "100%", md: "80%" },
                  paddingLeft: "0px",
                  margin: "0 auto",
                }}
              >
                <Box
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#15202b" : "#f9f9f9",
                    padding: "20px",
                    borderRadius: "8px",
                    margin: "20px",
                    width: "100% ",
                    mt: 0,
                    mb: 2,
                  }}
                >
                  <Grid container spacing={3}>
                    {(showAllCategories
                      ? Object.entries(grammarConcepts)
                      : Object.entries(grammarConcepts).filter(([key]) =>
                          initialCategories.includes(key),
                        )
                    ).map(([category, concepts]) => (
                      <Grid item xs={12} md={3} key={category}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "500",
                            mb: 1,
                            textTransform: "capitalize",

                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.7)"
                                : "rgba(0, 0, 0, 0.6)",
                          }}
                        >
                          {category.replace("_", " ")}:
                        </Typography>
                        <FormGroup>
                          {(expandedCategories[category]
                            ? concepts
                            : concepts.slice(0, 4)
                          ).map((concept) => (
                            <CustomFormControlLabel
                              theme={theme}
                              key={concept.id}
                              control={<Checkbox />}
                              label={
                                <Box>
                                  <Typography sx={{ fontSize: "0.975rem" }}>
                                    {concept.label}
                                  </Typography>
                                </Box>
                              }
                              checked={
                                selectedConcepts[category]?.[concept.id] ||
                                false
                              }
                              onChange={() =>
                                handleConceptChange(category, concept.id)
                              }
                            />
                          ))}
                        </FormGroup>
                        {concepts.length > 4 && (
                          <Button
                            onClick={() =>
                              setExpandedCategories((prev) => ({
                                ...prev,
                                [category]: !prev[category],
                              }))
                            }
                            sx={{
                              mt: 0.5,
                              textTransform: "none",
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.6)"
                                  : "rgba(0, 0, 0, 0.6)",
                              padding: "2px 8px",
                              minWidth: "auto",
                              fontSize: "0.75rem",
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.8)"
                                    : "rgba(0, 0, 0, 0.8)",
                              },
                              ml: 5.5,
                            }}
                            endIcon={
                              expandedCategories[category] ? (
                                <KeyboardArrowUpIcon
                                  sx={{ fontSize: "0.875rem" }}
                                />
                              ) : (
                                <KeyboardArrowDownIcon
                                  sx={{ fontSize: "0.875rem" }}
                                />
                              )
                            }
                          >
                            {expandedCategories[category]
                              ? "Visa färre"
                              : "Visa fler"}
                          </Button>
                        )}
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <Button
                          onClick={() =>
                            setShowAllCategories(!showAllCategories)
                          }
                          sx={{
                            mt: 0.5,
                            textTransform: "none",
                            color: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.6)"
                                : "rgba(0, 0, 0, 0.8)",
                            padding: "2px 8px",
                            minWidth: "auto",
                            fontSize: "0.95rem",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.8)"
                                  : "rgba(0, 0, 0, 0.8)",
                            },
                          }}
                          endIcon={
                            expandedCategories[showAllCategories] ? (
                              <KeyboardArrowUpIcon sx={{ fontSize: "1rem" }} />
                            ) : (
                              <KeyboardArrowDownIcon
                                sx={{ fontSize: "1rem" }}
                              />
                            )
                          }
                          endIcon={
                            showAllCategories ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )
                          }
                        >
                          {showAllCategories
                            ? "Visa färre kategorier"
                            : "Visa alla grammatiska koncept"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Button
                    variant={
                      theme.palette.mode === "light" ? "purp" : "contained"
                    }
                    onClick={handleGenerateGrammar}
                    disabled={isGenerating}
                    sx={{
                      mt: 6,
                    }}
                    startIcon={
                      isGenerating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <GenerateIcon />
                      )
                    }
                  >
                    {isGenerating ? "Genererar..." : "Generera lektion"}
                  </Button>
                </Box>
              </Box>
            </AnimatedStep>
          </>
        )}

        {currentStep === 3 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(1);
                setLessonData(null);
                setSelectedConcepts({});
              }}
              show={currentStep !== 1}
            />
            {lessonData && (
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  mt: 10,
                }}
              >
                <Box
                  sx={{
                    ...styles.freshBox,
                    flex: "1 1 auto",
                    padding: "30px",

                    width: viewMode ? "90%" : "100%",
                    ...(viewMode ? { flex: "0 0 70%" } : { flex: "1 1 auto" }),
                    margin: "0 auto",
                    mt: viewMode ? 0 : 0,
                  }}
                >
                  <SaveLessonDialog
                    key={titleText}
                    open={saveDialogOpen}
                    onClose={() => setSaveDialogOpen(false)}
                    onSave={handleSaveWithComment}
                    theme={theme}
                    lessonName={titleText}
                    lessonObject={lessonData}
                    lessonType="GrammarLesson.js"
                  />
                  {/* Title */}
                  {isEditingTitle ? (
                    <TextField
                      value={titleText}
                      onChange={(e) => setTitleText(e.target.value)}
                      onBlur={() => {
                        setIsEditingTitle(false);
                        showSnackbar("Titeln har uppdaterats", "success");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setIsEditingTitle(false);
                          showSnackbar("Titeln har uppdaterats", "success");
                        }
                      }}
                      autoFocus
                      fullWidth
                      variant="standard"
                      sx={{
                        backgroundColor: "transparent",
                        "& .MuiInputBase-input": {
                          textAlign: "center",
                          fontWeight: "600",
                          fontSize: "2.2em",
                        },
                      }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-block",
                        margin: "0 auto",
                        width: "100%",
                        mb: 3,
                        mt: 0,
                        ":hover .edit-icon": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Typography
                        variant="h1"
                        sx={{
                          fontWeight: "600",
                          fontSize: "2.2em",
                          cursor: "pointer",
                          textAlign: "center",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        {titleText || " "}
                        <Box
                          component="span"
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.2s",
                            display: "inline-flex",
                            gap: 0.5,
                          }}
                          className="edit-icon"
                        >
                          <Tooltip title="Redigera" placement="top">
                            <IconButton
                              size="small"
                              onClick={() => setIsEditingTitle(true)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Typography>
                    </Box>
                  )}
                  <Grid container spacing={3} sx={{ mt: 6 }}>
                    {lessonData.sentences.map((item, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card
                          sx={{
                            display: "flex",
                            height: "100%",
                            "&:hover .delete-button": {
                              opacity: 1,
                              visibility: "visible",
                            },
                            borderRadius: 2,
                            padding: "2px",
                            position: "relative",
                            "&:before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "4px",
                              background:
                                "linear-gradient(90deg, primary.light, primary.main)",
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                            },
                          }}
                        >
                          <IconButton
                            className="delete-button"
                            size="small"
                            onClick={() => {
                              const updatedSentences =
                                lessonData.sentences.filter(
                                  (_, i) => i !== index,
                                );
                              setLessonData({
                                ...lessonData,
                                sentences: updatedSentences,
                              });
                              showSnackbar(
                                "Meningen har tagits bort",
                                "success",
                              );
                            }}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              opacity: 0,
                              visibility: "hidden",
                              transition: "all 0.2s ease",
                              zIndex: 1,
                            }}
                          >
                            <Tooltip title="Ta bort mening" placement="top">
                              <DeleteOutlineIcon fontSize="small" />
                            </Tooltip>
                          </IconButton>

                          <CardContent
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              //justifyContent: 'center',
                              width: "100%",

                              minHeight: "120px",
                              position: "relative",
                              padding: "16px",
                            }}
                          >
                            {/* Original Sentence */}
                            <Box
                              sx={{
                                "&:hover .controls": {
                                  opacity: 1,
                                },
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: showTranslation ? 2 : 0,
                              }}
                            >
                              {editingExampleId === index ? (
                                <TextField
                                  fullWidth
                                  value={item.sentence}
                                  onChange={(e) => {
                                    const updatedSentences = [
                                      ...lessonData.sentences,
                                    ];
                                    updatedSentences[index] = {
                                      ...item,
                                      sentence: e.target.value,
                                    };
                                    setLessonData({
                                      ...lessonData,
                                      sentences: updatedSentences,
                                    });
                                  }}
                                  onBlur={() => {
                                    setEditingExampleId(null);
                                    showSnackbar(
                                      "Meningen har uppdaterats",
                                      "success",
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      setEditingExampleId(null);
                                      showSnackbar(
                                        "Meningen har uppdaterats",
                                        "success",
                                      );
                                    }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <Typography
                                  variant="h6"
                                  sx={{
                                    flex: 1,
                                  }}
                                >
                                  {item.sentence}
                                  <Box
                                    component="span"
                                    className="controls"
                                    sx={{
                                      opacity: 0,
                                      transition: "opacity 0.2s",
                                      display: "inline-flex",
                                      gap: 0.5,
                                      ml: 1,
                                    }}
                                  >
                                    <Tooltip title="Redigera" placement="top">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setEditingExampleId(index)
                                        }
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Typography>
                              )}
                            </Box>

                            {/* Translation if enabled */}
                            {showTranslation && (
                              <Box
                                sx={{
                                  "&:hover .controls": {
                                    opacity: 1,
                                  },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {editingTranslationId === index ? (
                                  <TextField
                                    fullWidth
                                    value={item.translation}
                                    onChange={(e) => {
                                      const updatedSentences = [
                                        ...lessonData.sentences,
                                      ];
                                      updatedSentences[index] = {
                                        ...item,
                                        translation: e.target.value,
                                      };
                                      setLessonData({
                                        ...lessonData,
                                        sentences: updatedSentences,
                                      });
                                    }}
                                    onBlur={() => {
                                      setEditingTranslationId(null);
                                      showSnackbar(
                                        "Översättningen har uppdaterats",
                                        "success",
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        setEditingTranslationId(null);
                                        showSnackbar(
                                          "Översättningen har uppdaterats",
                                          "success",
                                        );
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      flex: 1,
                                    }}
                                  >
                                    {item.translation}
                                    <Box
                                      component="span"
                                      className="controls"
                                      sx={{
                                        opacity: 0,
                                        transition: "opacity 0.2s",
                                        display: "inline-flex",
                                        gap: 0.5,
                                        ml: 1,
                                      }}
                                    >
                                      <Tooltip
                                        title="Redigera översättning"
                                        placement="top"
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            setEditingTranslationId(index)
                                          }
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Typography>
                                )}
                              </Box>
                            )}

                            <Box
                              sx={{
                                position: "absolute",
                                right: showTranslation ? "45px" : "5px",
                                bottom: "8px",
                              }}
                            >
                              <Voice
                                content={item.sentence}
                                fontSize={24}
                                userId={user.uid}
                                language={language}
                              />
                            </Box>

                            {/* Voice Controls */}
                            {showTranslation && (
                              <>
                                <Box
                                  sx={{
                                    position: "absolute",
                                    right: "5px",
                                    bottom: "8px",
                                  }}
                                >
                                  <Voice
                                    content={item.translation}
                                    fontSize={24}
                                    userId={user.uid}
                                    language={translationLanguage}
                                  />
                                </Box>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      mb: 4,
                    }}
                  >
                    <Button
                      variant={
                        theme.palette.mode === "light" ? "purp" : "contained"
                      }
                      startIcon={<AddIcon />}
                      sx={{ mt: 5 }}
                      onClick={() => {
                        const newSentence = {
                          sentence: "Ny exempelmening",
                          ...(showTranslation && {
                            translation: "Översättningen här",
                          }),
                          analysis: "Grammatisk analys kommer här",
                        };
                        setLessonData({
                          ...lessonData,
                          sentences: [...lessonData.sentences, newSentence],
                        });
                        showSnackbar("Ny mening har lagts till", "success");
                      }}
                    >
                      Lägg till mening
                    </Button>
                  </Box>
                  {/* Story Section */} {/* Story Section */}
                  <Card
                    sx={{
                      mt: 10,
                      mb: 4,
                      background:
                        theme.palette.mode === "light" ? "#f5f5f5" : "#1e1e1e",
                      borderRadius: "15px",
                      padding: "20px",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: "600",
                          fontSize: "25px",
                          display: "block",
                          mb: 3,
                        }}
                      >
                        Berättelse
                      </Typography>
                      {/* Original Story */}
                      <Box
                        sx={{
                          "&:hover .controls": {
                            opacity: 1,
                          },
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        {isEditingStory ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={lessonData.story}
                            onChange={(e) => {
                              setLessonData({
                                ...lessonData,
                                story: e.target.value,
                              });
                            }}
                            onBlur={() => {
                              setIsEditingStory(false);
                              showSnackbar(
                                "Berättelsen har uppdaterats",
                                "success",
                              );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                setIsEditingStory(false);
                                showSnackbar(
                                  "Berättelsen har uppdaterats",
                                  "success",
                                );
                              }
                            }}
                            autoFocus
                            sx={{
                              "& .MuiInputBase-input": {
                                lineHeight: 1.8,
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body1"
                            sx={{
                              lineHeight: 1.8,
                              flex: 1,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            {lessonData.story}
                            <Box
                              component="span"
                              className="controls"
                              sx={{
                                opacity: 0,
                                transition: "opacity 0.2s",
                                display: "inline-flex",
                                gap: 0.5,
                                ml: 1,
                                mt: 0.5,
                              }}
                            >
                              <Voice
                                content={lessonData.story}
                                fontSize={20}
                                userId={user.uid}
                                language={language}
                                tooltipPlacement="top"
                              />
                              <Tooltip
                                title="Redigera berättelse"
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setIsEditingStory(true)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Typography>
                        )}
                      </Box>

                      {/* Story Translation if enabled */}
                      {showTranslation && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Översättning
                          </Typography>
                          <Box
                            sx={{
                              "&:hover .controls": {
                                opacity: 1,
                              },
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            {isEditingStoryTranslation ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={lessonData.storyTranslation}
                                onChange={(e) => {
                                  setLessonData({
                                    ...lessonData,
                                    storyTranslation: e.target.value,
                                  });
                                }}
                                onBlur={() =>
                                  setIsEditingStoryTranslation(false)
                                }
                                autoFocus
                                sx={{
                                  "& .MuiInputBase-input": {
                                    lineHeight: 1.8,
                                  },
                                }}
                              />
                            ) : (
                              <Typography
                                variant="body1"
                                sx={{
                                  lineHeight: 1.8,
                                  flex: 1,
                                  color: "text.secondary",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                {lessonData.storyTranslation}
                                <Box
                                  component="span"
                                  className="controls"
                                  sx={{
                                    opacity: 0,
                                    transition: "opacity 0.2s",
                                    display: "inline-flex",
                                    gap: 0.5,
                                    ml: 1,
                                    mt: 0.5,
                                  }}
                                >
                                  <Voice
                                    content={lessonData.storyTranslation}
                                    fontSize={20}
                                    userId={user.uid}
                                    language={translationLanguage}
                                    tooltipPlacement="top"
                                  />
                                  <Tooltip
                                    title="Redigera översättning"
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setIsEditingStoryTranslation(true)
                                      }
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  <Card
                    sx={{
                      mt: 10,
                      mb: 4,
                      background:
                        theme.palette.mode === "light"
                          ? "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)"
                          : "linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 100%)",
                      borderRadius: "15px",
                      padding: "20px",
                      position: "relative",
                      "&:before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          "linear-gradient(90deg, primary.light, primary.main)",
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                      },
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: "600",
                          fontSize: "25px",
                          display: "block",
                          mb: 3,
                        }}
                        gutterBottom
                      >
                        Facit
                      </Typography>
                      {lessonData.sentences.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 3,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "bold",
                              mb: 1,
                            }}
                          >
                            Mening {index + 1}:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              mb: 1,
                            }}
                          >
                            "{item.sentence}"
                          </Typography>
                          {showTranslation && (
                            <Typography
                              variant="body1"
                              sx={{
                                mb: 1,
                                color: "text.secondary",
                              }}
                            >
                              "{item.translation}"
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {item.analysis}
                          </Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Box>
                {!viewMode && (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        ...styles.lessonControlPanelNuevo2,

                        "& .MuiButton-root": {
                          ...styles.lessonControlPanelNewButtons2,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        Spara
                      </Typography>
                      <Button
                        variant="print"
                        startIcon={<SaveAsIcon />}
                        sx={{ ml: 2 }}
                        onClick={handleSaveLessonClick}
                      >
                        Spara
                      </Button>

                      <Button
                        variant={
                          theme.palette.mode === "light" ? "print" : "contained"
                        }
                        startIcon={<PrintIcon />}
                        onClick={() =>
                          handlePrintGrammarLesson(
                            lessonData,
                            titleText,
                            showTranslation,
                            theme,
                          )
                        }
                      >
                        Skriv ut
                      </Button>
                      <PrintAsPdf
                        buttonRef={pdfButtonRef}
                        onPrint={() =>
                          handlePrintGrammarLesson(
                            lessonData,
                            titleText,
                            showTranslation,
                            theme,
                          )
                        }
                        sx={{ ml: 2 }}
                      />

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Dela
                      </Typography>
                      <ShareLink
                        buttonRef={shareLinkButtonRef}
                        key={lessonId}
                        lessonId={lessonId}
                        lessonData={lessonData}
                        user={user}
                        lessonType={"GrammarLesson.js"}
                        onCopy={(message, severity) =>
                          showSnackbar(message, severity)
                        }
                      />

                      <Button
                        variant="success"
                        sx={{}}
                        onClick={() => setShareDialogOpenFriends(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med vänner
                      </Button>
                      <Button
                        variant="success"
                        sx={{
                          mt: 2,
                          ml: 2,
                        }}
                        onClick={() => setShareDialogOpen(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med elever
                      </Button>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Visa
                      </Typography>
                      <Tooltip
                        title="Hur lektionen ser ut för eleverna"
                        placement="top"
                      >
                        <Button
                          variant="success"
                          onClick={() => {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                            toggleViewMode();
                          }}
                          startIcon={
                            searchParams.get("v") === "t" ? (
                              <PersonOutlineIcon />
                            ) : (
                              <PersonOutlineIcon />
                            )
                          }
                        >
                          {searchParams.get("v") === "t"
                            ? "Redigeringsläge"
                            : "Elevläge"}
                        </Button>
                      </Tooltip>

                      <Box>
                        <MoreToCome user={user} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={titleText || ""}
          lessonObject={lessonData}
          lessonType={"GrammarLesson.js"}
        />

        <ShareDialog
          open={shareDialogOpenFriends}
          onClose={() => setShareDialogOpenFriends(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={titleText || ""}
          lessonObject={lessonData}
          lessonType={"GrammarLesson.js"}
          shareWithFriends={true}
        />

        <LoadingBackdrop
          isOpen={isSharing}
          imagePath="/img/AI.png"
          loadingText={"Delar lektionen..."}
        />

        <LoadingDialog
          open={isGenerating}
          onClose={() => {}}
          progress={progress}
          progressLabel={"Genererar lektion — tar 30-60 sekunder..."}
          title="Genererar lektion. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />

        <LessonDrawer
          userId={user.uid}
          lessonType="GrammarLesson.js"
          theme={theme}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          lessonIcon={MenuBookIcon}
          onSelectLesson={(lesson) => {
            setLessonData(lesson.data);
            setTitleText(lesson.lessonName);
            setDrawerOpen(false);

            showSnackbar(`Lektionen har laddats in.`, "success");
            document
              .querySelector("hr")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        />

        <WelcomeDialogLessons
          key={currentStep}
          lessonType="GrammarLesson.js"
          open={currentStep === 3}
          title="Välkommen till Grammatik!"
          imageSrc="https://sportkuriren.se/wp-content/uploads/grammar.png"
          imageSrcMaxWidth="50%"
          items={[
            {
              icon: "Edit",
              text: "Du kan redigera, lägga till eller ta bort meningarna",
              color: "#ff9e7d",
            },
            {
              icon: "VolumeUp",
              text: "Klicka på flaggorna för att läsa upp meningen på respektive språk",
              color: "#ff9e7d",
            },
            {
              icon: "PersonOutline",
              text: "Klicka på Elevläge för att se hur lektionen ser ut för eleverna",

              color: "#ff6b6b",
            },
            {
              icon: "Share",
              text: "Dela lektionen via en länk som elever (inget elevkonto krävs) kan gå in på  eller via Studera.AI (elevkonto krävs)",

              color: "#ff6b6b",
            },
            {
              icon: "SaveAs",
              text: "Glöm inte att spara lektionen i din lektionsbank så du när som helst kan gå tillbaka och redigera",
              color: "#ff9e7d",
            },
            {
              icon: "Print",
              text: "Du kan även skriva ut lektionen eller spara som PDF",
              color: "#ff6b6b",
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default GrammarLesson;
